import { Grid, Pagination } from '@mui/material';
import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentAndGroupsTableType } from '@Src/_basic/object/AccountRightType';
import { PageInfo } from '@Src/_basic/object/PageInfoType';

import useGetAllFactories from '@Src/_basic/apiHooks/useGetAllFactories';
import YatungConfirmAlert from '@Src/_basic/components/YatungConfirmAlert';
import YatungFadeInOut from '@Src/_basic/components/YatungFadeInOut';
import YatungPage from '@Src/_basic/components/YatungPage';
import { updateReducer } from '@Src/_basic/helpers/useUpdateReducer';
import { RoleApi } from '@Src/_basic/protocol/accountRights/fetAuth/role/RoleApi';
import { useRequestSaving } from '@Src/redux/requestSaving/requestSavingActions';
import { useLocation } from 'react-router-dom';
import RoleMaintenanceCreateCard from '../RoleMaintenanceCreateCard';
import RoleMaintenanceUpdateCard from '../RoleMaintenanceUpdateCard';
import RoleMaintenanceSearchBar from './RoleMaintenanceSearchBar';
import RoleMaintenanceTable from './RoleMaintenanceTable';

export type FilterValue = {
  inputValues: string;
  factory: number;
};

type Query = FilterValue & {
  page: number;
};

export default function RoleMaintenanceCard() {
  const { t: i18T } = useTranslation();

  const location = useLocation();
  const { request, setSaveRequest } = useRequestSaving<Query>();
  const { withoutAllFactoryOption } = useGetAllFactories();

  const effectRef = useRef<boolean>(false);
  const [isVisable, setIsVisable] = useState<boolean>(false);
  const [modelOpen, setModelOpen] = useState<'CREATE' | 'UPDATE' | 'DELETE' | null>(null);

  const [roleData, setRoleData] = useState<Array<ContentAndGroupsTableType>>([]);
  const [selectedRoleData, setSelectedRoleData] = useState<ContentAndGroupsTableType | undefined>(undefined);

  const initFilterValue = useMemo(() => {
    const { inputValues = '', factory = 0 } =
      location.pathname === request?.page && request?.request ? request.request : {};

    return {
      inputValues,
      factory,
    };
  }, [location.pathname, request]);

  const initPageInfo = useMemo(
    () => ({
      page: location.pathname === request?.page && request?.request ? request.request.page : 1,
      pageSize: 10,
      total: 0,
      totalCount: 0,
    }),
    [location.pathname, request],
  );

  const [filterValue, filterDispatch] = useReducer<Reducer<FilterValue, Partial<FilterValue>>>(
    updateReducer,
    initFilterValue,
  );
  const [pageInfo, pageDispatch] = useReducer<Reducer<PageInfo, Partial<PageInfo>>>(updateReducer, initPageInfo);

  const postGetRoleList = useCallback(
    (page: number) => {
      if (!filterValue.factory) return;
      RoleApi.postGetRightsRoles(
        {
          pageNumber: page - 1,
          pageSize: pageInfo.pageSize,
          attrs: [
            {
              code: 'factory',
              attrVal: filterValue.factory.toString(),
            },
          ],
          ...(filterValue?.inputValues && { keywords: [filterValue.inputValues] }),
        },
        (_data) => {
          setRoleData(
            _data.content.map((item, index) => ({
              ...item,
              index: index + 1 + (page - 1) * pageInfo.pageSize,
            })),
          );
          pageDispatch({
            page,
            total: _data.totalPages,
          });
          setSaveRequest({
            page: location.pathname,
            request: {
              ...filterValue,
              page,
            },
          });
          setIsVisable(true);
        },
      );
    },
    [filterValue, pageInfo.pageSize, setSaveRequest, location.pathname],
  );

  const updateAndDeleteModel = useCallback((data: ContentAndGroupsTableType, model: 'UPDATE' | 'DELETE') => {
    setModelOpen(model);
    setSelectedRoleData(data);
  }, []);

  const closeModel = useCallback(() => {
    setModelOpen(null);
    setSelectedRoleData(undefined);
  }, []);

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    pageDispatch({ page });
    postGetRoleList(page);
  };

  const handleChange = useCallback((file: keyof FilterValue, value: FilterValue[keyof FilterValue]) => {
    filterDispatch({ [file]: value });
    pageDispatch({ page: 1, total: 0 });
    setRoleData([]);
    setIsVisable(false);
  }, []);

  const getCurrentPageRoleList = useCallback(() => postGetRoleList(pageInfo.page), [pageInfo.page, postGetRoleList]);

  const postDeleteRoleById = useCallback(() => {
    if (!selectedRoleData) return;
    RoleApi.postDeleteRightsRoleById(selectedRoleData.id, getCurrentPageRoleList);
  }, [selectedRoleData, getCurrentPageRoleList]);

  useEffect(() => {
    if (!effectRef.current) {
      effectRef.current = true;
      getCurrentPageRoleList();
    }
  }, [effectRef]);

  return (
    <>
      <YatungPage
        title={i18T('USERSMANAGEMENU.ROLE_MAINTENANCE.ROUTE_TITLE')}
        body={
          <>
            <RoleMaintenanceSearchBar
              filterValue={filterValue}
              handleChange={handleChange}
              factoryOptions={withoutAllFactoryOption}
              postGetRoleList={getCurrentPageRoleList}
              openCreateCard={() => setModelOpen('CREATE')}
            />
            {
              <YatungFadeInOut isVisible={isVisable}>
                <>
                  <RoleMaintenanceTable
                    updateAndDeleteModel={updateAndDeleteModel}
                    roleData={roleData}
                    factoryOptions={withoutAllFactoryOption}
                  />
                  <Grid container justifyContent={'center'} item xs={11} sx={{ mt: 2 }}>
                    <Pagination
                      count={pageInfo.total}
                      page={pageInfo.page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Grid>
                </>
              </YatungFadeInOut>
            }
          </>
        }
        contentBgColor="#FFF"
      />
      {modelOpen === 'CREATE' && (
        <RoleMaintenanceCreateCard
          open={modelOpen === 'CREATE'}
          closeModel={closeModel}
          factoryOptions={withoutAllFactoryOption}
          postGetRoleList={getCurrentPageRoleList}
        />
      )}
      {modelOpen === 'UPDATE' && !!selectedRoleData && (
        <RoleMaintenanceUpdateCard
          open={modelOpen === 'UPDATE'}
          closeModel={closeModel}
          factoryOptions={withoutAllFactoryOption}
          selectedRoleData={selectedRoleData}
          postGetRoleList={getCurrentPageRoleList}
        />
      )}
      <YatungConfirmAlert isShowAlert={modelOpen === 'DELETE'} onClose={closeModel} onOk={postDeleteRoleById} />
    </>
  );
}
